'use client';
import React, { useState, useMemo, useEffect } from 'react';
import {
  Content,
  ImageFieldImage,
  KeyTextField,
  LinkField,
  RichTextField,
} from '@prismicio/client';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';

interface Client {
  logo: ImageFieldImage | null | undefined;
  case_study_link?: LinkField | null | undefined;
  brief_description?: RichTextField | null | undefined;
  button_label?: KeyTextField;
}

interface ClientCaseStudyButtonProps {
  isValidLink: boolean;
  button_label?: KeyTextField;
  case_study_link?: LinkField;
}

/**
 * Props for `ClientsList`.
 */
export type ClientsListProps = SliceComponentProps<Content.ClientsListSlice>;

const ClientCard = ({
  logo,
  case_study_link,
  brief_description,
  button_label,
}: Client) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isValidLink, setIsValidLink] = useState(false);

  useEffect(() => {
    if (case_study_link) {
      setIsValidLink(Object.keys(case_study_link).length > 1);
    }
  }, [case_study_link]);

  const CardContent = (
    <div
      className="group h-[170px] bg-white-gradient rounded-2xl flex justify-center items-center relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <PrismicNextImage field={logo} className="mx-4" />

      <ClientCaseStudyButton
        isValidLink={isValidLink}
        button_label={button_label}
        case_study_link={case_study_link ?? undefined}
      />

      {brief_description && brief_description?.length > 0 && (
        <div
          className={`w-full p-12 absolute top-4 left-0 rounded-2xl bg-white-gradient backdrop-blur-sm shadow-xl transition-all duration-150 transform ${
            isHovered
              ? 'opacity-100 translate-y-0 pointer-events-auto'
              : 'opacity-0 -translate-y-6 pointer-events-none'
          } z-10`}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div className="min-h-full w-full mx-4 text-primary-dark-grey">
            <PrismicNextImage field={logo} className="mb-8" />
            <PrismicRichText field={brief_description}></PrismicRichText>
            <ClientCaseStudyButton
              isValidLink={isValidLink}
              button_label={button_label}
              case_study_link={case_study_link ?? undefined}
            />
          </div>
        </div>
      )}
    </div>
  );

  return isValidLink ? (
    <PrismicNextLink field={case_study_link}>{CardContent}</PrismicNextLink>
  ) : (
    <>{CardContent}</>
  );
};

const ClientCaseStudyButton = ({
  isValidLink,
  button_label,
  case_study_link,
}: ClientCaseStudyButtonProps) => {
  return (
    <>
      {isValidLink && button_label && (
        <PrismicNextLink
          field={case_study_link}
          className="button-outline-gray flex justify-center items-center absolute right-2 bottom-1 pointer-events-auto"
        >
          <span>{button_label}</span>
        </PrismicNextLink>
      )}
    </>
  );
};

/**
 * Component for "ClientsList" Slices.
 */
const ClientsList = ({ slice }: ClientsListProps): JSX.Element => {
  const {
    primary: { title, button_label_view_study_case, clients },
  } = slice;

  const [visibleRows, setVisibleRows] = useState(2);

  const clientsGrid = useMemo(() => {
    const firstAlternateRow = 3;
    const secondAlternateRow = 2;
    const finalRowCount = 4;
    let rowAlternateGrid = firstAlternateRow;
    const grid: Client[][] = [];

    for (let i = 0; i < clients.length; ) {
      const remaining = clients.length - i;

      if (remaining > finalRowCount) {
        const rowSize = Math.min(rowAlternateGrid, remaining);
        grid.push(clients.slice(i, i + rowSize) as Client[]);
        i += rowSize;

        rowAlternateGrid =
          rowSize === secondAlternateRow
            ? firstAlternateRow
            : secondAlternateRow;
      } else {
        grid.push(clients.slice(i, i + remaining) as Client[]);
        break;
      }
    }

    return grid;
  }, [clients]);

  const handleViewMore = () => {
    setVisibleRows((prev) => prev + 2);
  };

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="text-white mx-auto px-5 md:px-16 pb-28 max-w-screen-2xl"
    >
      <div className="flex justify-center">
        <PrismicRichText field={title} />
      </div>

      <div className="mx-6 md:mx-0 mt-12">
        <div className="hidden md:block">
          {clientsGrid.map((row, rowIndex) => {
            let numberOfCols = 'md:grid-cols-3';

            if (row.length === 2) {
              numberOfCols = 'md:grid-cols-2';
            }

            if (row.length === 4) {
              numberOfCols = 'md:grid-cols-4';
            }

            return (
              <div
                key={rowIndex}
                className={`grid gap-2 mt-2 grid-cols-1 ${numberOfCols}`}
              >
                {row.map((client, colIndex) => (
                  <ClientCard
                    key={colIndex}
                    {...client}
                    button_label={button_label_view_study_case}
                  />
                ))}
              </div>
            );
          })}
        </div>

        <div className="block md:hidden">
          {clientsGrid.slice(0, visibleRows).map((row, rowIndex) => (
            <div key={rowIndex} className="grid gap-2 mt-2 grid-cols-1">
              {row.map((client, colIndex) => (
                <ClientCard
                  key={colIndex}
                  {...client}
                  button_label={button_label_view_study_case}
                />
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="block md:hidden">
        {visibleRows < clientsGrid.length && (
          <div className="flex justify-center mt-8">
            <button
              onClick={handleViewMore}
              className="button-bordered"
              aria-label="View more clients"
            >
              View More
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default ClientsList;
